import type {
  BetSource,
  TickerBet,
  TickerFilterOptions,
  TickerFilters,
  TickerPage,
  TickerStatusScope,
} from '../domain'
import { PREVIOUS_TICKER_STATUSES } from '../domain'
import { classifyTickerMarketType } from '../ticker/marketType'
import { DEMO_TICKER_BETS, type DemoTickerBet } from './fixtures'

/**
 * Demo-session ticker.
 *
 * Applies the same rules as `/v1/book/ticker` so a demo walkthrough matches a
 * live one: the status split, filters and server-side paging.
 */

const SETTLED_STATUSES = new Set<string>(PREVIOUS_TICKER_STATUSES)

function toWireBet(row: DemoTickerBet): TickerBet {
  const { stake: _stake, status: _status, ...wire } = row
  return wire
}

export function inScope(row: DemoTickerBet, source: BetSource, scope: TickerStatusScope): boolean {
  if (row.source !== source) return false
  const settled = SETTLED_STATUSES.has(row.status)
  return scope === 'previous' ? settled : !settled
}

function matches(row: DemoTickerBet, filters: TickerFilters): boolean {
  if (
    filters.members.length &&
    !filters.members.includes(row.memberId) &&
    !filters.members.includes(row.memberUsername)
  ) {
    return false
  }
  if (filters.search) {
    const q = filters.search.toLowerCase()
    const hay = `${row.memberUsername} ${row.eventName} ${row.marketName} ${row.selection} ${row.id}`
    if (!hay.toLowerCase().includes(q)) return false
  }
  if (filters.sport !== 'all' && row.sport !== filters.sport) return false
  // Dropdowns send the full label — equality matches Lab event_name / market pickers.
  // Substring would let "Match Odds" keep "Match Odds (Bookmaker)" on sportsbook lists
  // and confused operators who thought they had narrowed the book.
  if (filters.event && row.eventName !== filters.event) return false
  if (filters.market && row.marketName !== filters.market) return false
  if (
    filters.marketType !== 'all' &&
    classifyTickerMarketType(row.marketName) !== filters.marketType
  ) {
    return false
  }
  if (filters.side !== 'all' && row.side !== filters.side) return false
  if (filters.status !== 'all' && row.status !== filters.status) return false
  if (filters.currency !== 'all' && row.currency !== filters.currency) return false
  if (filters.stakeMin != null && row.stake < filters.stakeMin) return false
  if (filters.stakeMax != null && row.stake > filters.stakeMax) return false
  const day = row.placedAt?.slice(0, 10) ?? ''
  if (filters.from && day && day < filters.from) return false
  if (filters.to && day && day > filters.to) return false
  return true
}

export function queryDemoTicker(
  source: BetSource,
  scope: TickerStatusScope,
  filters: TickerFilters,
  catalog: DemoTickerBet[] = DEMO_TICKER_BETS,
): TickerPage {
  const filtered = catalog
    .filter((row) => inScope(row, source, scope))
    .filter((row) => matches(row, filters))
    .slice()
    .sort((a, b) => (a.placedAt ?? '') < (b.placedAt ?? '') ? 1 : (a.placedAt ?? '') > (b.placedAt ?? '') ? -1 : 0)

  const start = (filters.page - 1) * filters.pageSize
  return {
    rows: filtered.slice(start, start + filters.pageSize).map(toWireBet),
    total: filtered.length,
    page: filters.page,
    pageSize: filters.pageSize,
    asOf: new Date().toISOString(),
  }
}

export function demoFilterOptions(
  source: BetSource,
  scope: TickerStatusScope,
  catalog: DemoTickerBet[] = DEMO_TICKER_BETS,
): TickerFilterOptions {
  const rows = catalog.filter((row) => inScope(row, source, scope))
  const uniq = (values: string[]) => [...new Set(values.filter(Boolean))].sort()
  return {
    sports: uniq(rows.map((row) => row.sport)),
    events: uniq(rows.map((row) => row.eventName)),
    markets: uniq(rows.map((row) => row.marketName)),
  }
}
